import { useCallback, useEffect, useState } from "react";
import { useSaveNotification, type UseSaveNotificationResult } from "./useSaveNotification";

type DetailFetcher<TLoaded> = (id: string) => Promise<TLoaded>;
type DetailValidator = () => string | null;

export interface UseDetailFormOptions<TLoaded, TSaved = TLoaded> {
  id: string | undefined;
  fetcher: DetailFetcher<TLoaded>;
  onLoaded: (data: TLoaded) => void | Promise<void>;
  saver: () => Promise<TSaved>;
  onSaved: (data: TSaved) => void;
  /** Return a string to short-circuit `save()` with an error banner. */
  validate?: DetailValidator;
  /** Banner message on success. Defaults to "Saved." */
  successMessage?: string;
  notificationOptions?: Parameters<typeof useSaveNotification>[0];
}

export interface UseDetailFormResult<TSaved> {
  loading: boolean;
  error: string | null;
  saving: boolean;
  save: () => Promise<TSaved | null>;
  notification: UseSaveNotificationResult["notification"];
  saveLabel: string;
  clearNotification: () => void;
}

/**
 * Centralises the "fetch by id → apply → save → show banner" flow shared by
 * ProjectDetails / ConnectorDetails / TransformDefinitionDetails. The hook
 * intentionally does NOT own field state — each page's form shape differs and
 * the `useDirtyForm` baseline helper stays caller-side.
 */
export function useDetailForm<TLoaded, TSaved = TLoaded>(
  opts: UseDetailFormOptions<TLoaded, TSaved>,
): UseDetailFormResult<TSaved> {
  const { id, fetcher, onLoaded, saver, onSaved, validate, successMessage } = opts;
  const notif = useSaveNotification(opts.notificationOptions);
  // Loading is derived from `id` at first render: only start "loading" when an id
  // is present. This avoids a synchronous `setLoading(false)` inside the effect,
  // which the `react-hooks/set-state-in-effect` rule (correctly) flags.
  const [loading, setLoading] = useState<boolean>(() => id != null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await fetcher(id);
        if (cancelled) return;
        await onLoaded(data);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch-on-id-change; closed-over fetcher/onLoaded change every render and must not retrigger fetch.
  }, [id]);

  const save = useCallback(async (): Promise<TSaved | null> => {
    if (validate) {
      const msg = validate();
      if (msg) {
        notif.notifyError(msg);
        return null;
      }
    }
    setSaving(true);
    try {
      const data = await saver();
      onSaved(data);
      notif.notifySuccess(successMessage ?? "Saved.");
      return data;
    } catch (err) {
      notif.notifyErrorFromException(err);
      return null;
    } finally {
      setSaving(false);
    }
  }, [validate, saver, onSaved, notif, successMessage]);

  return {
    loading,
    error,
    saving,
    save,
    notification: notif.notification,
    saveLabel: notif.saveLabel,
    clearNotification: notif.clear,
  };
}
